import { roundRobin } from "./roundRobin";

/**
 * Round-robin for SINGLES. 
 * If more weeks in 'allDays' than the round-robin produces, we cycle back to the first round.
 *
 * @param {Object} params
 * @param {Object[]} params.members - A list of all member objects (with .id, .name, etc.).
 * @param {Object} params.sectionObj - An object like { "A": [memberIds], "B": [memberIds] }.
 * @param {Date[]} params.allDays - All the valid match dates in the season.
 * @param {string} params.pennantId - A string used to build the IDs for each week.
 * @returns {Array} An array of weekly draw objects (one for each date).
 */
export default function generateSinglesDraw({
  members,
  sectionObj,
  allDays,
  pennantId,
}) {
  const memberDetailsMap = members.reduce((acc, m) => {
    acc[m.id] = m;
    return acc;
  }, {});

  const finalDraw = [];

  // For each section in sectionObj, build a roundRobin of those members
  Object.entries(sectionObj || {}).forEach(([sectionKey, memberIds]) => {
    // Skip if fewer than 2 members in the section
    if (!memberIds || memberIds.length < 2) return;

    // Build participants array for roundRobin
    const participants = memberIds
      .map((mid) => memberDetailsMap[mid])
      .filter(Boolean) // ignore any missing members
      .map((player) => ({
        id: player.id,
        name: player.name,
        // you could store the player's own section as well
        section: sectionKey,
      }));

    // If after filtering we have < 2, skip
    if (participants.length < 2) return;

    // 1) Generate the roundRobin "rounds"
    const rounds = roundRobin(participants); 
    const totalRounds = rounds.length;

    // 2) For each date in allDays, pick the correct round
    allDays.forEach((day, idx) => {
      const roundIndex = idx % totalRounds; // cycle back as needed
      const matchups = rounds[roundIndex];

      // Build matches from these round matchups
      const matches = matchups.map(({ home, away }) => {
        if (!away || away.id === null) {
          // "Bye"
          return {
            team1: { name: home.name, id: home.id },
            team2: { name: "Bye", id: null },
          };
        }
        // normal
        return {
          team1: { name: home.name, id: home.id },
          team2: { name: away.name, id: away.id },
        };
      });

      // Add a "week" object
      finalDraw.push({
        section: sectionKey,
        week: idx + 1,
        date: day.toLocaleDateString(),
        matches,
        id: `${pennantId}_${sectionKey}_week-${idx + 1}`,
      });
    });
  });

  return finalDraw;
}
